import {
  RAW_MATERIAL_KIND_LABELS,
  type OpeningStock,
} from "@artisancode/api-types";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import {
  useCompleteOpeningStock,
  useCreateOpeningStock,
  useDeleteOpeningStock,
  useUpdateOpeningStock,
} from "@/hooks/use-opening-stock";
import { useProducts } from "@/hooks/use-products";
import { useRawMaterials } from "@/hooks/use-raw-materials";
import { useUoms } from "@/hooks/use-uoms";

import {
  computeLines,
  fromDocLine,
  toPayloadLines,
  type FormLine,
  type ItemInfo,
} from "./opening-stock-utils";

export function useOpeningStockSession(
  docId?: string,
  initialDoc?: OpeningStock,
) {
  const navigate = useNavigate();
  const { data: rawMaterialsRes } = useRawMaterials({
    per_page: 200,
    is_active: true,
  });
  const { data: productsRes } = useProducts({ per_page: 200 });
  const { data: uomsRes } = useUoms();

  const [date, setDate] = useState(
    () => initialDoc?.date ?? new Date().toISOString().slice(0, 10),
  );
  const [note, setNote] = useState(() => initialDoc?.note ?? "");
  const [rawLines, setLines] = useState<FormLine[]>(
    () => initialDoc?.lines.map(fromDocLine) ?? [],
  );

  const createMutation = useCreateOpeningStock();
  const updateMutation = useUpdateOpeningStock();
  const completeMutation = useCompleteOpeningStock();
  const deleteMutation = useDeleteOpeningStock();

  const { info, options, buildLine } = useMemo(() => {
    const rawMaterials = rawMaterialsRes?.items ?? [];
    const products = productsRes?.items ?? [];
    const uomNames = new Map((uomsRes?.items ?? []).map((u) => [u.id, u.name]));
    const infoMap = new Map<string, ItemInfo>([
      ...rawMaterials.map((r) => [r.id, r] as const),
      ...products.map((p) => [p.id, { cogs: p.cogs }] as const),
    ]);
    const lineMap = new Map<string, FormLine>();
    for (const rm of rawMaterials) {
      lineMap.set(`raw_material:${rm.id}`, {
        itemType: "raw_material",
        itemId: rm.id,
        itemName: rm.name,
        itemSku: rm.sku ?? null,
        kind: rm.kind,
        uomName: rm.uom?.name ?? "",
        qtyOnHand: 0,
        qtyRepacking: 0,
        unitCost: 0,
        subtotalCost: 0,
      });
    }
    for (const p of products) {
      lineMap.set(`product:${p.id}`, {
        itemType: "product",
        itemId: p.id,
        itemName: p.name,
        itemSku: p.sku ?? null,
        sourcingType: p.sourcingType,
        uomName: p.uomId ? (uomNames.get(p.uomId) ?? "") : "",
        qtyOnHand: 0,
        qtyRepacking: 0,
        unitCost: 0,
        subtotalCost: 0,
      });
    }
    return {
      info: (id: string) => infoMap.get(id),
      buildLine: (key: string) => lineMap.get(key),
      options: [
        ...rawMaterials.map((rm) => ({
          value: `raw_material:${rm.id}`,
          label: `${rm.name} (${RAW_MATERIAL_KIND_LABELS[rm.kind]})`,
          hint: rm.sku ?? undefined,
        })),
        ...products.map((p) => ({
          value: `product:${p.id}`,
          label: `${p.name} (Produk ${p.sourcingType === "produced" ? "Racikan" : "Beli"})`,
          hint: p.sku ?? undefined,
        })),
      ],
    };
  }, [rawMaterialsRes?.items, productsRes?.items, uomsRes?.items]);

  const lines = useMemo(() => computeLines(rawLines, info), [rawLines, info]);
  const totalCost = lines.reduce((sum, l) => sum + l.subtotalCost, 0);
  const picked = new Set(rawLines.map((l) => `${l.itemType}:${l.itemId}`));
  const availableOptions = options.filter((o) => !picked.has(o.value));

  const addLine = (key: string) => {
    const line = buildLine(key);
    if (line) setLines((prev) => [...prev, line]);
  };
  const removeLine = (index: number) =>
    setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (
    index: number,
    field: "qtyOnHand" | "qtyRepacking" | "unitCost",
    value: number,
  ) =>
    setLines((prev) =>
      prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)),
    );

  /** Creates or updates the draft and returns its id. */
  const save = async () => {
    const body = {
      date,
      note: note || undefined,
      lines: toPayloadLines(lines),
    };
    if (docId) {
      await updateMutation.mutateAsync({ id: docId, ...body });
      return docId;
    }
    return (await createMutation.mutateAsync({ body })).id;
  };

  // Errors are toasted by the global mutation handler.
  const saveDraft = async () => {
    try {
      const id = await save();
      toast.success("Draft Stok Awal disimpan");
      if (!docId) navigate(`/opening-stocks/${id}`, { replace: true });
    } catch {
      /* handled globally */
    }
  };

  const complete = async () => {
    try {
      const id = await save();
      // Move to the saved draft first, so a failed completion does not create another one.
      if (!docId) navigate(`/opening-stocks/${id}`, { replace: true });
      await completeMutation.mutateAsync(id);
      toast.success("Stok Awal selesai. Stok dan harga sudah tercatat.");
      return true;
    } catch {
      return false;
    }
  };

  const remove = async () => {
    if (!docId) return;
    try {
      await deleteMutation.mutateAsync(docId);
      toast.success("Draft Stok Awal dihapus");
      navigate("/opening-stocks", { replace: true });
    } catch {
      /* handled globally */
    }
  };

  return {
    date,
    setDate,
    note,
    setNote,
    lines,
    totalCost,
    availableOptions,
    addLine,
    removeLine,
    updateLine,
    saveDraft,
    complete,
    remove,
    isSaving: createMutation.isPending || updateMutation.isPending,
    isCompleting: completeMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
